import type { FC, ReactNode } from 'react';

import { cva, cx } from 'cva';
import {
  type Key,
  Tab as RACTab,
  TabList as RACTabList,
  TabPanel as RACTabPanel,
  TabPanels as RACTabPanels,
  type TabProps as RACTabProps,
  Tabs as RACTabs,
  type TabsProps as RACTabsProps,
} from 'react-aria-components/Tabs';

import { linkHrefProps } from '../../utils/link_href_props';

import styles from './tabs.module.css';

export type TabItemProps = {
  id: Key;
  title: string;
  href?: RACTabProps['href'];
  content?: ReactNode;
};

export type TabsProps = RACTabsProps & {
  items: TabItemProps[];
  spacing?: 'none' | 's' | 'm' | 'l';
  tabsListAriaLabel?: string;
  isContentScrollable?: boolean;
  /**
   * Pins the tab list to the top of its scroll container. Set
   * `--sticky-tab-list-offset` on a scroll ancestor to stack it beneath
   * another sticky element. Tactical; will be removed once `Tabs` is split
   * into composable subcomponents (FE-5896 —
   * https://linear.app/iconik/issue/FE-5896), which lets consumers wrap the
   * tab list with their own sticky wrapper.
   */
  stickyTabList?: boolean;
};

const tabsVariants = cva({
  base: styles.tabs,
  variants: {
    spacing: {
      none: undefined,
      s: styles.spacing_s,
      m: styles.spacing_m,
      l: styles.spacing_l,
    },
  },
  defaultVariants: {
    spacing: 'none',
  },
});

export const Tabs: FC<TabsProps> = ({
  items,
  selectedKey,
  onSelectionChange,
  className,
  tabsListAriaLabel,
  spacing = 'none',
  isContentScrollable = false,
  stickyTabList = false,
  ...props
}) => {
  const classes = tabsVariants({ spacing, className });
  const tabsClassName = cx([
    classes,
    { [styles.tabs_scrollable_content]: isContentScrollable },
  ]);
  const tabPanelsClassName = cx([
    styles.tab_panels,
    { [styles.tab_panels_scrollable]: isContentScrollable },
  ]);
  const tabListClassName = cx([
    styles.tab_list,
    { [styles.tab_list_sticky]: stickyTabList },
  ]);

  return (
    <RACTabs
      keyboardActivation="manual"
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChange}
      className={tabsClassName}
      {...props}
    >
      <RACTabList
        items={items}
        aria-label={tabsListAriaLabel ?? 'Tabs list'}
        className={tabListClassName}
      >
        {(item) => (
          <RACTab
            className={cx([styles.tab, { [styles.tab_link]: item.href }])}
            key={item.id}
            {...linkHrefProps(item.href)}
          >
            {item.title}
          </RACTab>
        )}
      </RACTabList>
      <RACTabPanels items={items} className={tabPanelsClassName}>
        {(item) => (
          <RACTabPanel className={styles.tab_panel}>{item.content}</RACTabPanel>
        )}
      </RACTabPanels>
    </RACTabs>
  );
};
